import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, inject, Directive, Input } from '@angular/core';
import { tuiCreateToken, tuiProvideOptions, tuiWithStyles, tuiIsString } from '@taiga-ui/cdk/utils/miscellaneous';
import { tuiButtonOptionsProvider } from '@taiga-ui/core/components/button';
import { tuiLinkOptionsProvider } from '@taiga-ui/core/components/link';
import * as i2 from '@taiga-ui/core/directives/appearance';
import { tuiAppearanceOptionsProvider, TuiWithAppearance } from '@taiga-ui/core/directives/appearance';
import * as i1 from '@taiga-ui/core/directives/icons';
import { TuiIcons, TuiWithIcons } from '@taiga-ui/core/directives/icons';
const ICONS = {
  info: '@tui.info',
  success: '@tui.circle-check',
  error: '@tui.circle-x',
  warning: '@tui.circle-alert',
  neutral: '@tui.info'
};
/** Default values for the notification options. */
const TUI_NOTIFICATION_DEFAULT_OPTIONS = {
  appearance: 'info',
  icon: appearance => ICONS[appearance] ?? '',
  size: 'l'
};
/**
 * Default parameters for notification alert component
 */
const TUI_NOTIFICATION_OPTIONS = tuiCreateToken(TUI_NOTIFICATION_DEFAULT_OPTIONS);
function tuiNotificationOptionsProvider(options) {
  return tuiProvideOptions(TUI_NOTIFICATION_OPTIONS, options, TUI_NOTIFICATION_DEFAULT_OPTIONS);
}
let TuiNotificationStyles = /*#__PURE__*/(() => {
  class TuiNotificationStyles {
    static {
      this.ɵfac = function TuiNotificationStyles_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiNotificationStyles)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiNotificationStyles,
        selectors: [["ng-component"]],
        hostAttrs: [1, "tui-notification"],
        decls: 0,
        vars: 0,
        template: function TuiNotificationStyles_Template(rf, ctx) {},
        styles: ["tui-notification,[tuiNotification]{-webkit-appearance:none;appearance:none;padding:0;border:0;background:none;font-size:inherit;line-height:inherit;position:relative;display:flex;max-block-size:100%;color:var(--tui-text-primary);gap:.5rem;padding:1rem;font:var(--tui-font-text-m);border-radius:var(--tui-radius-l);box-sizing:border-box;overflow:hidden;text-align:start;text-decoration:none}tui-notification:after,[tuiNotification]:after{font-size:1rem;margin:0 -.25rem 0 auto;align-self:center;color:var(--tui-text-tertiary)!important}tui-notification[data-size=s],[tuiNotification][data-size=s]{gap:.5rem;padding:.375rem .625rem;font:var(--tui-font-text-s);border-radius:var(--tui-radius-m)}tui-notification[data-size=s]:before,[tuiNotification][data-size=s]:before,tui-notification[data-size=s] tui-icon,[tuiNotification][data-size=s] tui-icon{font-size:1rem;margin-top:.125rem;margin-bottom:.125rem}tui-notification[data-size=s] [tuiTitle],[tuiNotification][data-size=s] [tuiTitle]{font:var(--tui-font-text-s);font-weight:700}tui-notification[data-size=s] [tuiSubtitle],[tuiNotification][data-size=s] [tuiSubtitle]{font:var(--tui-font-text-s)}tui-notification[data-size=s] [tuiSubtitle]+*,[tuiNotification][data-size=s] [tuiSubtitle]+*{gap:1rem;margin:.375rem 0 .25rem}tui-notification[data-size=s]>[tuiIconButton],[tuiNotification][data-size=s]>[tuiIconButton]{margin:-.375rem -.625rem -.375rem auto}tui-notification[data-size=m],[tuiNotification][data-size=m]{gap:.375rem;padding:.75rem;font:var(--tui-font-text-s);border-radius:var(--tui-radius-m)}tui-notification[data-size=m]:before,[tuiNotification][data-size=m]:before,tui-notification[data-size=m] tui-icon,[tuiNotification][data-size=m] tui-icon{font-size:1.25rem}tui-notification[data-size=m] [tuiTitle],[tuiNotification][data-size=m] [tuiTitle]{font:var(--tui-font-text-ui-m);font-weight:700}tui-notification[data-size=m] [tuiSubtitle],[tuiNotification][data-size=m] [tuiSubtitle]{font:var(--tui-font-text-s)}tui-notification[data-size=m] [tuiSubtitle]+*,[tuiNotification][data-size=m] [tuiSubtitle]+*{gap:1rem;margin:.625rem 0 .25rem}tui-notification[data-size=m]>[tuiIconButton],[tuiNotification][data-size=m]>[tuiIconButton]{margin:-.375rem -.25rem -.375rem auto}tui-notification [tuiTitle],[tuiNotification] [tuiTitle]{gap:.125rem;font:var(--tui-font-text-ui-l);font-weight:700}tui-notification [tuiSubtitle],[tuiNotification] [tuiSubtitle]{font:var(--tui-font-text-m)}tui-notification [tuiSubtitle]+*,[tuiNotification] [tuiSubtitle]+*{display:flex;align-items:center;gap:1.25rem;margin-top:.625rem;font:var(--tui-font-text-s)}tui-notification>[tuiIconButton],[tuiNotification]>[tuiIconButton]{box-shadow:none!important;background:transparent!important;margin:-.25rem -.25rem -.25rem auto}[tuiNotification]{cursor:pointer}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiNotificationStyles;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiNotification = /*#__PURE__*/(() => {
  class TuiNotification {
    constructor() {
      this.options = inject(TUI_NOTIFICATION_OPTIONS);
      this.nothing = tuiWithStyles(TuiNotificationStyles);
      this.icons = inject(TuiIcons);
      this.appearance = this.options.appearance;
      this.icon = this.options.icon;
      this.size = this.options.size;
    }
    ngOnInit() {
      this.refresh();
    }
    ngOnChanges() {
      this.refresh();
    }
    refresh() {
      this.icons.iconStart = tuiIsString(this.icon) ? this.icon : this.icon(this.appearance);
    }
    static {
      this.ɵfac = function TuiNotification_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiNotification)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiNotification,
        selectors: [["tui-notification"], ["a", "tuiNotification", ""], ["button", "tuiNotification", ""]],
        hostVars: 1,
        hostBindings: function TuiNotification_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-size", ctx.size);
          }
        },
        inputs: {
          appearance: "appearance",
          icon: "icon",
          size: "size"
        },
        features: [i0.ɵɵProvidersFeature([tuiAppearanceOptionsProvider(TUI_NOTIFICATION_OPTIONS), tuiLinkOptionsProvider({
          appearance: '',
          pseudo: true
        }), tuiButtonOptionsProvider({
          appearance: 'whiteblock',
          size: 's'
        })]), i0.ɵɵHostDirectivesFeature([i1.TuiWithIcons, i2.TuiWithAppearance]), i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return TuiNotification;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_NOTIFICATION_DEFAULT_OPTIONS, TUI_NOTIFICATION_OPTIONS, TuiNotification, tuiNotificationOptionsProvider };
